import type { PAGE_POSITIONS, PageType } from 'types';
import type { ProductListModule } from 'types/capi/modules';
import type { Affiliate } from 'types/capi/shared';

import AffiliatePageLink from '~/components/AffiliatePageLink';
import Image from '~/components/Image';
import ProductCard from '~/components/ProductCard';

type BaseProps = {
  backgroundColour?: 'bg-white' | 'bg-grey-tile';
  module: ProductListModule;
  // Rendered on both the homepage and the sale page, so the rank space is named by the caller —
  // the surface is what tells two same-shaped CMS modules apart.
  modulePosition?: number;
  moduleType?: string;
  pagePosition?: PAGE_POSITIONS;
};

type Props = BaseProps & { pageType: PageType };

export default function ProductList({
  module,
  modulePosition,
  moduleType,
  pagePosition = 'Featured Product Block',
  backgroundColour = 'bg-white',
  pageType
}: Props) {
  return (
    <div className={`w-full ${backgroundColour}`}>
      <div className="content-width flex flex-col items-center justify-center gap-6 py-10 md:gap-10">
        <div className="flex flex-col gap-2 self-stretch py-0 md:gap-10">
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-2 md:flex-row md:gap-6">
              <AffiliateLogo
                affiliate={module.cta.affiliate}
                className="w-16"
              />
              <div className="flex flex-col gap-2 text-sm md:text-base">
                <h2 className="text-title-lg flex flex-col justify-items-center text-left">
                  {module.header}
                </h2>
                {module.description}
              </div>
            </div>
          </div>
          <Products
            module={module}
            modulePosition={modulePosition}
            moduleType={moduleType}
            pagePosition={pagePosition}
            pageType={pageType}
          />
        </div>
        <CtaButton module={module} pagePosition={pagePosition} />
      </div>
    </div>
  );
}

function Products({
  module,
  modulePosition,
  moduleType,
  pagePosition,
  pageType
}: {
  module: ProductListModule;
  modulePosition?: number;
  moduleType?: string;
  pagePosition: PAGE_POSITIONS;
  pageType: PageType;
}) {
  return (
    <div className="custom-scrollbar flex overflow-x-scroll md:block md:overflow-x-auto">
      <div className="grid shrink-0 grid-cols-4 gap-2 md:gap-6">
        {module.items.map((product, index) => (
          <ProductCard.External
            key={`product-${product.id}-${index}`}
            product={product}
            padImage
            className="h-auto w-48 md:w-full"
            allowNotifications
            imageLoading="eager"
            cardSize="small"
            showNotificationCount={true}
            name="PRODUCT_CARD"
            modulePosition={modulePosition}
            moduleType={moduleType}
            pagePosition={pagePosition}
            position={index + 1}
            overrideInteractionPagePosition={true}
            pageType={pageType}
          />
        ))}
      </div>
    </div>
  );
}

function AffiliateLogo({
  affiliate,
  className = ''
}: {
  affiliate: Affiliate;
  className?: string;
}) {
  return (
    <Image
      src={affiliate.logo}
      className={`rounded ${className}`}
      alt={affiliate.name}
      loading="lazy"
      height={150}
      width={150}
      responsiveOptions={{ sm: { height: 150, pad: true, width: 150 } }}
      pad
    />
  );
}

function CtaButton({ module, pagePosition }: BaseProps) {
  return (
    <div className="flex h-full w-[360px] flex-col gap-2 font-bold">
      <AffiliatePageLink
        affiliate={module.cta.affiliate}
        urlTemplates={module.cta}
        buttonName="PRODUCT_CARD"
        pagePosition={pagePosition}
        variant="forest"
        className="flex h-12 items-center justify-center gap-2"
      >
        <AffiliateLogo affiliate={module.cta.affiliate} className="w-7" />
        {module.cta.text}
      </AffiliatePageLink>
    </div>
  );
}
